import { supabase } from './supabase';
import type {
  SourceModule,
  DiscoveredTable,
  DiscoveredColumn,
  DiscoveredVirtualField,
  SchemaDiscoveryResult,
} from '../types/mapping';
import { SOURCE_MODULES } from '../types/mapping';

const CACHE_DURATION_MS = 5 * 60 * 1000;

let cachedResult: SchemaDiscoveryResult | null = null;
let cacheTimestamp = 0;

interface RawColumnRow {
  table_schema: string;
  table_name: string;
  column_name: string;
  data_type: string;
  udt_name: string;
  is_nullable: string;
  column_default: string | null;
  ordinal_position: number;
  character_maximum_length: number | null;
}

async function fetchVirtualFields(): Promise<DiscoveredVirtualField[]> {
  try {
    const { data, error } = await supabase.rpc('cs_discover_virtual_fields');

    if (error) {
      console.warn(
        '[schemaDiscovery] cs_discover_virtual_fields unavailable:',
        error.message
      );
      return [];
    }

    return (data as DiscoveredVirtualField[]) || [];
  } catch (err) {
    console.warn('[schemaDiscovery] Exception fetching virtual fields:', err);
    return [];
  }
}

function attachVirtualFields(
  tables: DiscoveredTable[],
  virtualFields: DiscoveredVirtualField[]
): void {
  if (virtualFields.length === 0) return;

  const eavTable = tables.find(
    (t) => t.table_name === 'sm_school_attributes'
  );

  if (!eavTable) {
    console.warn(
      '[schemaDiscovery] Virtual fields discovered but sm_school_attributes not present in schema result'
    );
    return;
  }

  eavTable.virtual_columns = virtualFields;
}

export async function discoverSchema(
  forceRefresh = false
): Promise<SchemaDiscoveryResult> {
  if (
    !forceRefresh &&
    cachedResult &&
    Date.now() - cacheTimestamp < CACHE_DURATION_MS
  ) {
    return cachedResult;
  }

  try {
    const prefixes = Object.values(SOURCE_MODULES).map((m) => m.tablePrefix);

    const [schemaResponse, virtualFields] = await Promise.all([
      supabase.rpc('cs_discover_schema', { prefixes }),
      fetchVirtualFields(),
    ]);

    const { data: columns, error: columnsError } = schemaResponse;

    if (columnsError) {
      console.warn(
        'RPC cs_discover_schema not available, falling back to direct query:',
        columnsError.message
      );
      return await discoverSchemaFallback(virtualFields);
    }

    const result = groupColumnsIntoTables(columns as RawColumnRow[]);
    attachVirtualFields(result.tables, virtualFields);

    cachedResult = result;
    cacheTimestamp = Date.now();

    return result;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : 'Unknown schema discovery error';
    console.error('Schema discovery failed:', errorMessage);

    return {
      tables: [],
      discoveredAt: new Date().toISOString(),
      error: errorMessage,
    };
  }
}

async function discoverSchemaFallback(
  virtualFields: DiscoveredVirtualField[] = []
): Promise<SchemaDiscoveryResult> {
  try {
    const allTables: DiscoveredTable[] = [];

    for (const [sourceKey, config] of Object.entries(SOURCE_MODULES)) {
      const { data, error } = await supabase
        .from('information_schema.columns' as never)
        .select(
          'table_schema, table_name, column_name, data_type, udt_name, is_nullable, column_default, ordinal_position, character_maximum_length'
        )
        .eq('table_schema', 'public')
        .like('table_name', `${config.tablePrefix}%`);

      if (error) {
        console.warn(
          `Failed to discover ${config.label} tables:`,
          error.message
        );
        continue;
      }

      if (!data || data.length === 0) continue;

      const tableMap = new Map<string, DiscoveredTable>();

      for (const col of data as unknown as RawColumnRow[]) {
        if (!tableMap.has(col.table_name)) {
          tableMap.set(col.table_name, {
            table_name: col.table_name,
            table_schema: col.table_schema,
            source: sourceKey as SourceModule,
            columns: [],
            row_count_estimate: null,
            description: generateTableDescription(col.table_name),
          });
        }

        tableMap.get(col.table_name)!.columns.push(parseColumn(col));
      }

      for (const table of tableMap.values()) {
        table.columns.sort((a, b) => a.ordinal_position - b.ordinal_position);
        allTables.push(table);
      }
    }

    allTables.sort((a, b) => a.table_name.localeCompare(b.table_name));

    attachVirtualFields(allTables, virtualFields);

    const result: SchemaDiscoveryResult = {
      tables: allTables,
      discoveredAt: new Date().toISOString(),
      error: null,
    };

    cachedResult = result;
    cacheTimestamp = Date.now();

    return result;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : 'Fallback discovery failed';
    return {
      tables: [],
      discoveredAt: new Date().toISOString(),
      error: errorMessage,
    };
  }
}

function groupColumnsIntoTables(
  columns: RawColumnRow[]
): SchemaDiscoveryResult {
  const tableMap = new Map<string, DiscoveredTable>();

  for (const col of columns) {
    const tableKey = `${col.table_schema}.${col.table_name}`;

    if (!tableMap.has(tableKey)) {
      const source = determineSource(col.table_name);
      if (!source) continue;

      tableMap.set(tableKey, {
        table_name: col.table_name,
        table_schema: col.table_schema,
        source,
        columns: [],
        row_count_estimate: null,
        description: generateTableDescription(col.table_name),
      });
    }

    tableMap.get(tableKey)!.columns.push(parseColumn(col));
  }

  for (const table of tableMap.values()) {
    table.columns.sort((a, b) => a.ordinal_position - b.ordinal_position);
  }

  const tables = Array.from(tableMap.values()).sort((a, b) =>
    a.table_name.localeCompare(b.table_name)
  );

  return {
    tables,
    discoveredAt: new Date().toISOString(),
    error: null,
  };
}

function parseColumn(col: RawColumnRow): DiscoveredColumn {
  return {
    column_name: col.column_name,
    data_type: col.data_type,
    udt_name: col.udt_name,
    is_nullable: col.is_nullable === 'YES',
    column_default: col.column_default,
    ordinal_position: col.ordinal_position,
    character_maximum_length: col.character_maximum_length,
  };
}

function determineSource(tableName: string): SourceModule | null {
  for (const [key, config] of Object.entries(SOURCE_MODULES)) {
    if (tableName.startsWith(config.tablePrefix)) {
      return key as SourceModule;
    }
  }
  return null;
}

function generateTableDescription(tableName: string): string {
  const withoutPrefix = tableName.replace(/^(ad|sm)_/, '');
  return withoutPrefix
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export async function fetchPreviewRecords(
  tableName: string,
  limit = 10,
  filterColumn?: string,
  filterValue?: string
): Promise<{ records: Record<string, unknown>[]; error: string | null }> {
  try {
    let query = supabase.from(tableName).select('*').limit(limit);

    if (filterColumn && filterValue) {
      query = query.eq(filterColumn, filterValue);
    }

    const { data, error } = await query;

    if (error) {
      return { records: [], error: error.message };
    }

    return { records: (data as Record<string, unknown>[]) || [], error: null };
  } catch (err) {
    return {
      records: [],
      error: err instanceof Error ? err.message : 'Failed to fetch preview',
    };
  }
}

export function invalidateSchemaCache(): void {
  cachedResult = null;
  cacheTimestamp = 0;
}

export async function getTablesForSource(
  source: SourceModule
): Promise<DiscoveredTable[]> {
  const schema = await discoverSchema();
  return schema.tables.filter((t) => t.source === source);
}

export async function getTableColumns(
  tableName: string
): Promise<DiscoveredColumn[]> {
  const schema = await discoverSchema();
  const table = schema.tables.find((t) => t.table_name === tableName);
  return table?.columns || [];
}
